import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getConfig } from "../config";
import { apiErrorDetail } from "../http";
import {
  createAsset,
  updateAsset,
  removeAsset,
  createLiability,
  updateLiability,
  removeLiability,
  createTransaction,
  updateTransaction,
  removeTransaction,
  uploadAttachment,
  removeAttachment,
} from "../api/portfolio";

// Human label for save/delete toasts, by asset type.
function assetLabel(assetType) {
  if (assetType === "crypto") return "Crypto holding";
  if (assetType === "mutual_fund") return "Mutual funds";
  if (assetType === "us_stocks") return "US stocks";
  if (assetType === "gold") return "Gold";
  if (assetType === "govt_schemes") return "Govt schemes";
  if (assetType === "bank_accounts") return "Savings accounts";
  if (assetType === "loans") return "Loans";
  if (assetType === "bonds") return "Bonds";
  if (assetType === "travel_points") return "Travel points";
  return "Asset";
}

// Every write invalidates the dashboard payload and all snapshot ranges.
function useInvalidatePortfolio() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
      queryClient.invalidateQueries({ queryKey: ["snapshots"] }),
    ]);
}

/** Create (no id) or update (with id) an asset. Rejects on failure so
 *  dialogs can stay open and preserve the user's input. */
export function useSaveAsset() {
  const invalidate = useInvalidatePortfolio();
  return useMutation({
    mutationFn: ({ payload, id }) => (id ? updateAsset(id, payload) : createAsset(payload)),
    onSuccess: async (_data, { payload, id }) => {
      getConfig().notify.success(`${assetLabel(payload.asset_type)} ${id ? "updated" : "added"}!`);
      await invalidate();
    },
    onError: (error, { payload }) => {
      getConfig().notify.error(apiErrorDetail(error) || `Failed to save ${assetLabel(payload.asset_type).toLowerCase()}`);
    },
  });
}

export function useDeleteAsset() {
  const invalidate = useInvalidatePortfolio();
  return useMutation({
    mutationFn: (id) => removeAsset(id),
    onSuccess: async () => {
      getConfig().notify.success("Asset deleted");
      await invalidate();
    },
    onError: () => getConfig().notify.error("Failed to delete"),
  });
}

/** Readable message for a failed entry write. Ledger errors read "on 2
 *  Jan 2024, …" so they also fit after a prefix; shown alone they start
 *  with a capital. */
export function ledgerErrorMessage(error, fallback) {
  const msg = apiErrorDetail(error);
  return msg ? msg[0].toUpperCase() + msg.slice(1) : fallback;
}

// An entry changes the portfolio it belongs to, so its list refreshes too.
function useInvalidateLedger() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidatePortfolio();
  return (assetId) =>
    Promise.all([invalidate(), queryClient.invalidateQueries({ queryKey: ["transactions", assetId] })]);
}

/** Create (no id) or update (with id) a ledger entry. The server checks
 *  the whole history and its error says what doesn't add up; callers show
 *  it next to the form (ledgerErrorMessage), so there's no error toast. */
export function useSaveTransaction() {
  const invalidate = useInvalidateLedger();
  return useMutation({
    mutationFn: ({ assetId, id, payload }) =>
      id ? updateTransaction(assetId, id, payload) : createTransaction(assetId, payload),
    onSuccess: async (_data, { assetId, id }) => {
      getConfig().notify.success(`Entry ${id ? "updated" : "added"}`);
      await invalidate(assetId);
    },
  });
}

export function useDeleteTransaction() {
  const invalidate = useInvalidateLedger();
  return useMutation({
    mutationFn: ({ assetId, id }) => removeTransaction(assetId, id),
    onSuccess: async (_data, { assetId }) => {
      getConfig().notify.success("Entry deleted");
      await invalidate(assetId);
    },
  });
}

// A proof file changes its entry's list and the activity's 📎 counts.
function useInvalidateProofs() {
  const queryClient = useQueryClient();
  return (assetId, txId) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["attachments", txId] }),
      queryClient.invalidateQueries({ queryKey: ["transactions", assetId] }),
    ]);
}

/** Attach a proof file to an entry; errors are shown by the caller. */
export function useUploadAttachment() {
  const invalidate = useInvalidateProofs();
  return useMutation({
    mutationFn: ({ assetId, txId, file, kind }) => uploadAttachment(assetId, txId, file, kind),
    onSuccess: (_data, { assetId, txId }) => invalidate(assetId, txId),
  });
}

export function useRemoveAttachment() {
  const invalidate = useInvalidateProofs();
  return useMutation({
    mutationFn: ({ assetId, txId, id }) => removeAttachment(assetId, txId, id),
    onSuccess: (_data, { assetId, txId }) => invalidate(assetId, txId),
  });
}

export function useSaveLiability() {
  const invalidate = useInvalidatePortfolio();
  return useMutation({
    mutationFn: ({ payload, id }) => (id ? updateLiability(id, payload) : createLiability(payload)),
    onSuccess: async (_data, { id }) => {
      getConfig().notify.success(`Liability ${id ? "updated" : "added"}!`);
      await invalidate();
    },
    onError: (error) => getConfig().notify.error(apiErrorDetail(error) || "Failed to save liability"),
  });
}

export function useDeleteLiability() {
  const invalidate = useInvalidatePortfolio();
  return useMutation({
    mutationFn: (id) => removeLiability(id),
    onSuccess: async () => {
      getConfig().notify.success("Liability deleted");
      await invalidate();
    },
    onError: () => getConfig().notify.error("Failed to delete"),
  });
}
